// src/lib/MarkdownPlugin.ts
import {
  bindFirst,
  createTSlatePlugin,
  isUrl
} from "@udecode/plate";

// src/lib/utils/mapTypeUtils.ts
import "mdast-util-mdx";
var plateToMdastTypeMap = {
  // common elements
  p: "paragraph",
  heading: "heading",
  blockquote: "blockquote",
  code_block: "code",
  hr: "thematicBreak",
  equation: "math",
  inline_equation: "inlineMath",
  img: "image",
  list: "list",
  li: "listItem",
  table: "table",
  tr: "tableRow",
  th: "tableCell",
  td: "tableCell",
  a: "link",
  // common marks
  bold: "strong",
  italic: "emphasis",
  code: "inlineCode",
  text: "text",
  strikethrough: "delete",
  // plate only elements
  code_line: "code_line",
  column: "column",
  column_group: "column_group",
  toc: "toc",
  callout: "callout",
  toggle: "toggle",
  mention: "mention",
  date: "date",
  // plate only marks
  underline: "underline",
  suggestion: "suggestion",
  comment: "comment",
  superscript: "superscript",
  subscript: "subscript"
};
var getMdAstNodeType = (plateType) => {
  return plateToMdastTypeMap[plateType] ?? plateType;
};
var mdastToPlateTypeMap = {
  // common elements
  paragraph: "p",
  heading: "heading",
  thematicBreak: "hr",
  blockquote: "blockquote",
  list: "list",
  listItem: "li",
  table: "table",
  tableRow: "tr",
  tableCell: "td",
  code: "code_block",
  link: "a",
  image: "img",
  math: "equation",
  inlineMath: "inline_equation",
  // markdown only
  html: "html",
  yaml: "yaml",
  linkReference: "linkReference",
  imageReference: "imageReference",
  footnoteReference: "footnoteReference",
  definition: "definition",
  footnoteDefinition: "footnoteDefinition",
  break: "break",
  // decoration
  emphasis: "italic",
  strong: "bold",
  delete: "strikethrough",
  inlineCode: "code",
  text: "text",
  // mdx
  mdxFlowExpression: "mdxFlowExpression",
  mdxTextExpression: "mdxTextExpression",
  mdxJsxFlowElement: "mdxJsxFlowElement",
  mdxJsxTextElement: "mdxJsxTextElement",
  mdxjsEsm: "mdxjsEsm"
};
var getPlateNodeType = (mdastType) => {
  return mdastToPlateTypeMap[mdastType] ?? mdastType;
};

// src/lib/serializer/convertNodesSerialize.ts
import {
  TextApi
} from "@udecode/plate";

// src/lib/serializer/utils/getCustomMark.ts
var getCustomMark = (options) => {
  if (!options?.rules) {
    return [];
  }
  return Object.entries(options.rules).filter(([_, parser]) => parser?.mark).map(([key]) => key);
};

// src/lib/serializer/utils/getMergedOptionsSerialize.ts
var getMergedOptionsSerialize = (editor, options) => {
  const {
    allowedNodes: PluginAllowedNodes,
    allowNode: PluginAllowNode,
    disallowedNodes: PluginDisallowedNodes,
    remarkPlugins: PluginRemarkPlugins,
    rules: PluginRules
  } = editor.getOptions(MarkdownPlugin);
  const mergedRules = Object.assign(
    {},
    defaultRules,
    options?.rules ?? PluginRules
  );
  return {
    allowedNodes: options?.allowedNodes ?? PluginAllowedNodes,
    allowNode: options?.allowNode ?? PluginAllowNode,
    disallowedNodes: options?.disallowedNodes ?? PluginDisallowedNodes,
    editor,
    remarkPlugins: options?.remarkPlugins ?? PluginRemarkPlugins ?? [],
    rules: mergedRules,
    value: options?.value ?? editor.children
  };
};

// src/lib/serializer/utils/getSerializerByKey.ts
var getSerializerByKey = (key, options) => {
  const nodes = options.rules;
  return nodes?.[key]?.serialize === void 0 ? defaultRules[key]?.serialize : nodes?.[key]?.serialize;
};

// src/lib/serializer/utils/unreachable.ts
var unreachable = (value) => {
  console.warn(`Unreachable code: ${JSON.stringify(value)}`);
};

// src/lib/serializer/convertTextsSerialize.ts
var basicMarkdownMarks = ["italic", "bold", "strikethrough", "code"];
var convertTextsSerialize = (slateTexts, options, key) => {
  const customLeaf = getCustomMark(options);
  const mdastTexts = [];
  const starts = [];
  let ends = [];
  let textTemp = "";
  for (let j = 0; j < slateTexts.length; j++) {
    const cur = slateTexts[j];
    textTemp += cur.text;
    const prevStarts = starts.slice();
    const prevEnds = ends.slice();
    const prev = slateTexts[j - 1];
    const next = slateTexts[j + 1];
    ends = [];
    [
      ...basicMarkdownMarks,
      // exclude repeated marks
      ...customLeaf.filter((k) => !basicMarkdownMarks.includes(k))
    ].forEach((k) => {
      if (cur[k]) {
        if (!prev?.[k]) {
          starts.push(k);
        }
        if (!next?.[k]) {
          ends.push(k);
        }
      }
    });
    const endsToRemove = starts.reduce(
      (acc, k, kIndex) => {
        if (ends.includes(k)) {
          acc.push({ key: k, index: kIndex });
        }
        return acc;
      },
      []
    );
    if (starts.length > 0) {
      let bef = "";
      let aft = "";
      if (endsToRemove.length === 1 && (prevStarts.toString() !== starts.toString() || // https://github.com/inokawa/remark-slate-transformer/issues/90
      prevEnds.includes("italic") && ends.includes("bold")) && starts.length - endsToRemove.length === 0) {
        while (textTemp.startsWith(" ")) {
          bef += " ";
          textTemp = textTemp.slice(1);
        }
        while (textTemp.endsWith(" ")) {
          aft += " ";
          textTemp = textTemp.slice(0, -1);
        }
      }
      let res = {
        type: "text",
        value: textTemp
      };
      textTemp = "";
      starts.slice().reverse().forEach((k) => {
        const nodeParser = getSerializerByKey(k, options);
        if (nodeParser) {
          const node = nodeParser(cur, options);
          res = {
            ...node,
            children: [res]
          };
        }
        switch (k) {
          case "bold": {
            res = {
              children: [res],
              type: "strong"
            };
            break;
          }
          case "code": {
            res = {
              type: "inlineCode",
              value: res.value
            };
            break;
          }
          case "italic": {
            res = {
              children: [res],
              type: "emphasis"
            };
            break;
          }
          case "strikethrough": {
            res = {
              children: [res],
              type: "delete"
            };
            break;
          }
          default: {
            unreachable(k);
            break;
          }
        }
      });
      const arr = [];
      if (bef.length > 0) {
        arr.push({ type: "text", value: bef });
      }
      arr.push(res);
      if (aft.length > 0) {
        arr.push({ type: "text", value: aft });
      }
      mdastTexts.push(...arr);
    }
    if (endsToRemove.length > 0) {
      endsToRemove.reverse().forEach((e) => {
        starts.splice(e.index, 1);
      });
    } else {
      mdastTexts.push({ type: "text", value: textTemp });
      textTemp = "";
    }
  }
  if (textTemp) {
    mdastTexts.push({ type: "text", value: textTemp });
    textTemp = "";
  }
  return mergeTexts(mdastTexts);
};
var mergeTexts = (nodes) => {
  const res = [];
  for (const cur of nodes) {
    const last = res.at(-1);
    if (last && last.type === cur.type) {
      if (last.type === "text") {
        last.value += cur.value;
      } else if (last.type === "inlineCode") {
        last.value += cur.value;
      } else {
        last.children = mergeTexts(
          last.children.concat(cur.children)
        );
      }
    } else {
      if (cur.type === "text" && cur.value === "") continue;
      res.push(cur);
    }
  }
  return res;
};

// src/lib/serializer/indentListToMdastTree.ts
function indentListToMdastTree(nodes, options) {
  if (nodes.length === 0) {
    throw new Error("Cannot create a list from empty nodes");
  }
  const root = {
    children: [],
    ordered: nodes[0].listStyleType === "decimal",
    start: nodes[0].listStart,
    type: "list"
  };
  const indentStack = [{ indent: nodes[0].indent, list: root, parent: null }];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const currentIndent = node.indent;
    while (indentStack.length > 1 && indentStack.at(-1).indent > currentIndent) {
      indentStack.pop();
    }
    const stackTop = indentStack.at(-1);
    if (!stackTop) {
      throw new Error("Stack should never be empty");
    }
    const listItem = {
      checked: null,
      children: [
        {
          children: convertNodesSerialize(
            node.children,
            options
          ),
          type: "paragraph"
        }
      ],
      type: "listItem"
    };
    if (node.listStyleType === "todo" && node.checked !== void 0) {
      listItem.checked = node.checked;
    }
    stackTop.list.children.push(listItem);
    const nextNode = nodes[i + 1];
    if (nextNode && nextNode.indent > currentIndent) {
      const nestedList = {
        children: [],
        ordered: nextNode.listStyleType === "decimal",
        start: nextNode.listStart,
        type: "list"
      };
      listItem.children.push(nestedList);
      indentStack.push({
        indent: nextNode.indent,
        list: nestedList,
        parent: listItem
      });
    }
  }
  return root;
}

// src/lib/serializer/convertNodesSerialize.ts
var convertNodesSerialize = (nodes, options) => {
  const mdastNodes = [];
  let textQueue = [];
  const listBlock = [];
  for (let i = 0; i <= nodes.length; i++) {
    const n = nodes[i];
    if (n && TextApi.isText(n)) {
      if (shouldIncludeText(n, options)) {
        textQueue.push(n);
      }
    } else {
      if (textQueue.length > 0) {
        mdastNodes.push(
          ...convertTextsSerialize(
            textQueue,
            options
          )
        );
      }
      textQueue = [];
      if (!n) continue;
      if (!shouldIncludeNode(n, options)) {
        continue;
      }
      if (n?.type === "p" && "listStyleType" in n) {
        listBlock.push(n);
        const next = nodes[i + 1];
        const isNextIndent = next && next.type === "p" && "listStyleType" in next;
        if (!isNextIndent) {
          mdastNodes.push(indentListToMdastTree(listBlock, options));
          listBlock.length = 0;
        }
      } else {
        const node = buildMdastNode(n, options);
        if (node) {
          mdastNodes.push(node);
        }
      }
    }
  }
  return mdastNodes;
};
var buildMdastNode = (node, options) => {
  let key = node.type;
  if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(node.type)) {
    key = "heading";
  }
  if (["ol", "ul"].includes(node.type)) {
    key = "list";
  }
  const nodeParser = getSerializerByKey(key, options);
  if (nodeParser) {
    return nodeParser(node, options);
  }
  unreachable(node);
};
var shouldIncludeText = (text, options) => {
  const { allowedNodes, allowNode, disallowedNodes } = options;
  if (allowedNodes && disallowedNodes && allowedNodes.length > 0 && disallowedNodes.length > 0) {
    throw new Error("Cannot combine allowedNodes with disallowedNodes");
  }
  for (const [key, value] of Object.entries(text)) {
    if (key === "text") continue;
    if (allowedNodes) {
      if (!allowedNodes.includes(key) && value) {
        return false;
      }
    } else if (disallowedNodes?.includes(key) && value) {
      return false;
    }
  }
  if (allowNode?.serialize) {
    return allowNode.serialize(text);
  }
  return true;
};
var shouldIncludeNode = (node, options) => {
  const { allowedNodes, allowNode, disallowedNodes } = options;
  if (!node.type) return true;
  if (allowedNodes && disallowedNodes && allowedNodes.length > 0 && disallowedNodes.length > 0) {
    throw new Error("Cannot combine allowedNodes with disallowedNodes");
  }
  if (allowedNodes) {
    if (!allowedNodes.includes(node.type)) {
      return false;
    }
  } else if (disallowedNodes?.includes(node.type)) {
    return false;
  }
  if (allowNode?.serialize) {
    return allowNode.serialize(node);
  }
  return true;
};

// src/lib/serializer/serializeInlineMd.ts
import remarkStringify from "remark-stringify";
import { unified } from "unified";
var serializeInlineMd = (editor, options) => {
  const mergedOptions = getMergedOptionsSerialize(editor, options);
  const toRemarkProcessor = unified().use(mergedOptions.remarkPlugins ?? []).use(remarkStringify);
  if (options?.value?.length === 0) return "";
  const serializedContent = toRemarkProcessor.stringify({
    children: convertTextsSerialize(mergedOptions.value, {}),
    type: "root"
  });
  return serializedContent;
};

// src/lib/serializer/serializeMd.ts
import remarkStringify2 from "remark-stringify";
import { unified as unified2 } from "unified";
import { xml } from "zeed-dom";
var serializeMdxJsxElement = (node) => {
  if (node.type === "text") {
    return node.value || "";
  }
  if (node.type === "mdxJsxTextElement" || node.type === "mdxJsxFlowElement") {
    const attributes = node.attributes.reduce(
      (acc, attr) => {
        acc[attr.name] = attr.value;
        return acc;
      },
      {}
    );
    const content = node.children.map((child) => serializeMdxJsxElement(child)).join("");
    return xml(node.name, attributes, content);
  }
  return "";
};
var serializeMd = (editor, options) => {
  const mergedOptions = getMergedOptionsSerialize(editor, options);
  const { remarkPlugins, value } = mergedOptions;
  const toRemarkProcessor = unified2().use(remarkPlugins ?? []).use(remarkStringify2, {
    // Configure remark-stringify to handle MDX JSX elements
    handlers: {
      mdxJsxFlowElement: (node) => {
        return serializeMdxJsxElement(node);
      },
      mdxJsxTextElement: (node) => {
        return serializeMdxJsxElement(node);
      }
    }
  });
  return toRemarkProcessor.stringify(
    slateToMdast({
      children: value,
      options: mergedOptions
    })
  );
};
var slateToMdast = ({
  children,
  options
}) => {
  const ast = {
    children: convertNodesSerialize(children, options),
    type: "root"
  };
  return ast;
};

// src/lib/rules/defaultRules.ts
var defaultRules = {
  a: {
    deserialize: (mdastNode, deco, options) => {
      return {
        children: convertChildrenDeserialize(mdastNode.children, deco, options),
        type: "a",
        url: mdastNode.url
      };
    },
    serialize: (node, options) => {
      return {
        children: convertNodesSerialize(
          node.children,
          options
        ),
        type: "link",
        url: node.url
      };
    }
  },
  blockquote: {
    deserialize: (mdastNode, deco, options) => {
      const children = mdastNode.children.length > 0 ? mdastNode.children.flatMap((paragraph) => {
        if (paragraph.type === "paragraph") {
          return convertChildrenDeserialize(
            paragraph.children,
            deco,
            options
          );
        }
        if ("children" in paragraph) {
          return convertChildrenDeserialize(
            paragraph.children,
            deco,
            options
          );
        }
        return [{ text: "" }];
      }) : [{ text: "" }];
      const flattenedChildren = children.flatMap(
        (child) => child.type === "blockquote" ? child.children : [child]
      );
      return {
        children: flattenedChildren,
        type: "blockquote"
      };
    },
    serialize: (node, options) => {
      return {
        children: convertNodesSerialize(
          node.children,
          options
        ),
        type: "blockquote"
      };
    }
  },
  bold: {
    mark: true,
    deserialize: (mdastNode, deco, options) => {
      return convertTextsDeserialize(mdastNode, deco, options);
    }
  },
  code: {
    mark: true,
    deserialize: (mdastNode, deco, options) => {
      return {
        ...deco,
        code: true,
        text: mdastNode.value
      };
    }
  },
  code_block: {
    deserialize: (mdastNode, deco, options) => {
      return {
        children: (mdastNode.value || "").split("\n").map((line) => ({
          children: [{ text: line }],
          type: "code_line"
        })),
        lang: mdastNode.lang ?? void 0,
        type: "code_block"
      };
    },
    serialize: (node) => {
      return {
        lang: node.lang,
        type: "code",
        value: node.children.map((child) => child.children.map((c) => c.text).join("")).join("\n")
      };
    }
  },
  date: {
    serialize: ({ date }) => {
      return {
        type: "text",
        value: date ?? ""
      };
    }
  },
  equation: {
    deserialize: (mdastNode, deco, options) => {
      return {
        children: [{ text: "" }],
        texExpression: mdastNode.value,
        type: "equation"
      };
    },
    serialize: (node) => {
      return {
        type: "math",
        value: node.texExpression
      };
    }
  },
  heading: {
    deserialize: (mdastNode, deco, options) => {
      const headingType = {
        1: "h1",
        2: "h2",
        3: "h3",
        4: "h4",
        5: "h5",
        6: "h6"
      };
      return {
        children: convertChildrenDeserialize(mdastNode.children, deco, options),
        type: headingType[mdastNode.depth]
      };
    },
    serialize: (node, options) => {
      const depthMap = {
        h1: 1,
        h2: 2,
        h3: 3,
        h4: 4,
        h5: 5,
        h6: 6
      };
      return {
        children: convertNodesSerialize(
          node.children,
          options
        ),
        depth: depthMap[node.type],
        type: "heading"
      };
    }
  },
  highlight: {
    mark: true,
    deserialize: (mdastNode, deco, options) => {
      return convertChildrenDeserialize(
        mdastNode.children,
        { highlight: true, ...deco },
        options
      );
    },
    serialize(slateNode, options) {
      return {
        attributes: [
          {
            name: "style",
            type: "mdxJsxAttribute",
            value: "background-color: yellow;"
          }
        ],
        children: [{ type: "text", value: slateNode.text }],
        name: "highlight",
        type: "mdxJsxTextElement"
      };
    }
  },
  hr: {
    deserialize: () => {
      return {
        children: [{ text: "" }],
        type: "hr"
      };
    },
    serialize: () => {
      return { type: "thematicBreak" };
    }
  },
  html: {
    deserialize: (mdastNode, deco, options) => {
      return {
        text: (mdastNode.value || "").replaceAll("<br />", "\n")
      };
    }
  },
  img: {
    deserialize: (mdastNode, deco, options) => {
      return {
        caption: [{ text: mdastNode.alt }],
        children: [{ text: "" }],
        type: "img",
        url: mdastNode.url
      };
    },
    serialize: ({ caption, url }) => {
      const image = {
        alt: caption ? caption.map((c) => c.text).join("") : void 0,
        title: caption ? caption.map((c) => c.text).join("") : void 0,
        type: "image",
        url
      };
      return { children: [image], type: "paragraph" };
    }
  },
  inline_equation: {
    deserialize(mdastNode) {
      return {
        children: [{ text: "" }],
        texExpression: mdastNode.value,
        type: "inline_equation"
      };
    },
    serialize: (node) => {
      return {
        type: "inlineMath",
        value: node.texExpression
      };
    }
  },
  italic: {
    deserialize: (mdastNode, deco, options) => {
      return convertTextsDeserialize(mdastNode, deco, options);
    }
  },
  list: {
    deserialize: (mdastNode, deco, options) => {
      const isStandardList = !options.editor?.pluginList.some(
        (p) => p.key === "listStyleType"
      );
      if (isStandardList) {
        const children = mdastNode.children.map((child) => {
          if (child.type === "listItem") {
            return {
              children: child.children.map((itemChild) => {
                if (itemChild.type === "paragraph") {
                  return {
                    children: convertChildrenDeserialize(
                      itemChild.children,
                      deco,
                      options
                    ),
                    type: "lic"
                  };
                }
                return convertChildrenDeserialize(
                  [itemChild],
                  deco,
                  options
                )[0];
              }),
              type: "li"
            };
          }
          return convertChildrenDeserialize([child], deco, options)[0];
        });
        return {
          children,
          type: mdastNode.ordered ? "ol" : "ul"
        };
      }
      const parseListItems = (listNode, indent = 1, startIndex2 = 1) => {
        const items = [];
        const isOrdered = !!listNode.ordered;
        const listStyleType = isOrdered ? "decimal" : "disc";
        listNode.children?.forEach((listItem, index) => {
          if (listItem.type !== "listItem") return;
          const [paragraph, ...subLists] = listItem.children || [];
          const result = paragraph ? buildSlateNode(paragraph, deco, options) : { children: [{ text: "" }], type: "p" };
          const itemNodes = Array.isArray(result) ? result : [result];
          itemNodes.forEach((node) => {
            const itemContent = {
              ...node,
              indent,
              type: node.type || "p"
            };
            itemContent.listStyleType = listStyleType;
            if (isOrdered) {
              itemContent.listStart = startIndex2 + index;
            }
            items.push(itemContent);
          });
          subLists.forEach((subNode) => {
            if (subNode.type === "list") {
              const subListStart = subNode.start || 1;
              const nestedItems = parseListItems(
                subNode,
                indent + 1,
                subListStart
              );
              items.push(...nestedItems);
            } else {
              const result2 = buildSlateNode(subNode, deco, options);
              if (Array.isArray(result2)) {
                items.push(
                  ...result2.map((item) => ({
                    ...item,
                    indent: indent + 1
                  }))
                );
              } else {
                items.push({
                  ...result2,
                  indent: indent + 1
                });
              }
            }
          });
        });
        return items;
      };
      const startIndex = mdastNode.start || 1;
      return parseListItems(mdastNode, 1, startIndex);
    },
    serialize: (node, options) => {
      const isOrdered = node.type === "ol";
      const serializeListItems = (children) => {
        const items = [];
        let currentItem = null;
        for (const child of children) {
          if (child.type === "li") {
            if (currentItem) {
              items.push(currentItem);
            }
            currentItem = {
              children: [],
              type: "listItem"
            };
            for (const liChild of child.children) {
              if (liChild.type === "lic") {
                currentItem.children.push({
                  children: convertNodesSerialize(liChild.children, options),
                  type: "paragraph"
                });
              } else if (liChild.type === "ol" || liChild.type === "ul") {
                currentItem.children.push({
                  children: serializeListItems(liChild.children),
                  ordered: liChild.type === "ol",
                  type: "list"
                });
              }
            }
          }
        }
        if (currentItem) {
          items.push(currentItem);
        }
        return items;
      };
      return {
        children: serializeListItems(node.children),
        ordered: isOrdered,
        type: "list"
      };
    }
  },
  listItem: {
    deserialize: (mdastNode, deco, options) => {
      const children = mdastNode.children.map((child) => {
        if (child.type === "paragraph") {
          return {
            children: convertChildrenDeserialize(child.children, deco, options),
            type: "lic"
          };
        }
        return convertChildrenDeserialize([child], deco, options)[0];
      });
      return {
        children,
        type: "li"
      };
    },
    serialize: (node, options) => {
      return {
        children: convertNodesSerialize(node.children, options),
        type: "listItem"
      };
    }
  },
  mention: {
    serialize: ({ value }) => {
      return {
        type: "text",
        value
      };
    }
  },
  p: {
    deserialize: (node, deco, options) => {
      const isKeepLineBreak = options.splitLineBreaks;
      const children = convertChildrenDeserialize(node.children, deco, options);
      const paragraphType = getPlateNodeType("paragraph");
      const splitBlockTypes = /* @__PURE__ */ new Set(["img"]);
      const elements = [];
      let inlineNodes = [];
      const flushInlineNodes = () => {
        if (inlineNodes.length > 0) {
          elements.push({
            children: inlineNodes,
            type: paragraphType
          });
          inlineNodes = [];
        }
      };
      children.forEach((child) => {
        const { type } = child;
        if (type && splitBlockTypes.has(type)) {
          flushInlineNodes();
          elements.push(child);
        } else if (isKeepLineBreak && "text" in child && typeof child.text === "string") {
          const textParts = child.text.split("\n");
          const isSingleLineBreak = child.text === "\n" && inlineNodes.length === 0;
          if (isSingleLineBreak) {
            inlineNodes.push({ ...child, text: "" });
            flushInlineNodes();
            return;
          }
          textParts.forEach((part, index, array) => {
            const isNotFirstPart = index > 0;
            const isNotLastPart = index < array.length - 1;
            if (isNotFirstPart) {
              flushInlineNodes();
            }
            if (part) {
              inlineNodes.push({ ...child, text: part });
            }
            if (isNotLastPart) {
              flushInlineNodes();
            }
          });
        } else {
          inlineNodes.push(child);
        }
      });
      flushInlineNodes();
      return elements.length === 1 ? elements[0] : elements;
    },
    serialize: (node, options) => {
      return {
        children: convertNodesSerialize(
          node.children,
          options
        ),
        type: "paragraph"
      };
    }
  },
  strikethrough: {
    mark: true,
    deserialize: (mdastNode, deco, options) => {
      return convertTextsDeserialize(mdastNode, deco, options);
    }
  },
  table: {
    deserialize: (node, deco, options) => {
      const rows = node.children?.map((row, rowIndex) => {
        return {
          children: row.children?.map((cell) => {
            const cellType = rowIndex === 0 ? "th" : "td";
            return {
              children: convertChildrenDeserialize(
                cell.children,
                deco,
                options
              ).map((child) => {
                if (!child.type) {
                  return {
                    children: [child],
                    type: "p"
                  };
                }
                return child;
              }),
              type: cellType
            };
          }) || [],
          type: "tr"
        };
      }) || [];
      return {
        children: rows,
        type: "table"
      };
    },
    serialize: (node, options) => {
      return {
        children: convertNodesSerialize(
          node.children,
          options
        ),
        type: "table"
      };
    }
  },
  td: {
    serialize: (node, options) => {
      return {
        children: convertNodesSerialize(
          node.children,
          options
        ),
        type: "tableCell"
      };
    }
  },
  text: {
    deserialize: (mdastNode, deco) => {
      return {
        ...deco,
        text: mdastNode.value
      };
    }
  },
  th: {
    serialize: (node, options) => {
      return {
        children: convertNodesSerialize(
          node.children,
          options
        ),
        type: "tableCell"
      };
    }
  },
  toc: {
    deserialize: (mdastNode, deco, options) => {
      return {
        children: convertChildrenDeserialize(mdastNode.children, deco, options),
        type: "toc"
      };
    },
    serialize: (node, options) => {
      return {
        attributes: [],
        children: convertNodesSerialize(node.children, options),
        name: "toc",
        type: "mdxJsxFlowElement"
      };
    }
  },
  tr: {
    serialize: (node, options) => {
      return {
        children: convertNodesSerialize(
          node.children,
          options
        ),
        type: "tableRow"
      };
    }
  },
  underline: {
    mark: true,
    deserialize: (mdastNode, deco, options) => {
      return convertChildrenDeserialize(
        mdastNode.children,
        { underline: true, ...deco },
        options
      );
    },
    serialize(slateNode, options) {
      return {
        attributes: [],
        children: [{ type: "text", value: slateNode.text }],
        name: "u",
        type: "mdxJsxTextElement"
      };
    }
  }
};

// src/lib/deserializer/utils/getDeserializerByKey.ts
var getDeserializerByKey = (key, options) => {
  const rules = options.rules;
  return rules?.[key]?.deserialize === void 0 ? defaultRules[key]?.deserialize : rules?.[key]?.deserialize;
};

// src/lib/deserializer/utils/customMdxDeserialize.ts
var customMdxDeserialize = (mdastNode, deco, options) => {
  if (mdastNode.name === "br") {
    const parserKey = mdastNode.name;
    const nodeParserDeserialize = getDeserializerByKey(parserKey, options);
    if (nodeParserDeserialize)
      return nodeParserDeserialize(mdastNode, deco, options);
    return [{ text: "\n" }];
  }
  if (mdastNode.name === "u") {
    const parserKey = "underline";
    const nodeParserDeserialize = getDeserializerByKey(parserKey, options);
    if (nodeParserDeserialize)
      return nodeParserDeserialize(mdastNode, deco, options);
  }
  const customJsxElementKey = mdastNode.name;
  if (customJsxElementKey) {
    const nodeParserDeserialize = getDeserializerByKey(
      customJsxElementKey,
      options
    );
    if (nodeParserDeserialize)
      return nodeParserDeserialize(mdastNode, deco, options);
  }
};

// src/lib/deserializer/utils/stripMarkdown.ts
var stripMarkdownBlocks = (text) => {
  text = text.replaceAll(/^#{1,6}\s+/gm, "");
  text = text.replaceAll(/^\s*>\s?/gm, "");
  text = text.replaceAll(/^([*_-]){3,}\s*$/gm, "");
  text = text.replaceAll(/^(\s*)([*+-]|\d+\.)\s/gm, "$1");
  text = text.replaceAll(/^```[\S\s]*?^```/gm, "");
  text = text.replaceAll("<br>", "\n");
  return text;
};
var stripMarkdownInline = (text) => {
  text = text.replaceAll(/(\*\*|__)(.*?)\1/g, "$2");
  text = text.replaceAll(/(\*|_)(.*?)\1/g, "$2");
  text = text.replaceAll(/\[([^\]]+)]\(([^)]+)\)/g, "$1");
  text = text.replaceAll(/`(.+?)`/g, "$1");
  text = text.replaceAll("&nbsp;", " ");
  text = text.replaceAll("&lt;", "<");
  text = text.replaceAll("&gt;", ">");
  text = text.replaceAll("&amp;", "&");
  return text;
};
var stripMarkdown = (text) => {
  text = stripMarkdownBlocks(text);
  text = stripMarkdownInline(text);
  return text;
};

// src/lib/deserializer/utils/deserializeInlineMd.ts
var deserializeInlineMd = (editor, text) => {
  const leadingSpaces = /^\s*/.exec(text)?.[0] || "";
  const trailingSpaces = /\s*$/.exec(text)?.[0] || "";
  const strippedText = stripMarkdownBlocks(text.trim());
  const fragment = [];
  if (leadingSpaces) {
    fragment.push({ text: leadingSpaces });
  }
  if (strippedText) {
    fragment.push(
      ...editor.getApi(MarkdownPlugin).markdown.deserialize(strippedText)[0].children
    );
  }
  if (trailingSpaces) {
    fragment.push({ text: trailingSpaces });
  }
  return fragment;
};

// src/lib/deserializer/utils/getMergedOptionsDeserialize.ts
var getMergedOptionsDeserialize = (editor, options) => {
  const {
    allowedNodes: PluginAllowedNodes,
    allowNode: PluginAllowNode,
    disallowedNodes: PluginDisallowedNodes,
    remarkPlugins: PluginRemarkPlugins,
    rules: PluginRules
  } = editor.getOptions(MarkdownPlugin);
  const mergedRules = Object.assign(
    {},
    defaultRules,
    options?.rules ?? PluginRules
  );
  return {
    allowedNodes: options?.allowedNodes ?? PluginAllowedNodes,
    allowNode: options?.allowNode ?? PluginAllowNode,
    disallowedNodes: options?.disallowedNodes ?? PluginDisallowedNodes,
    editor,
    memoize: options?.memoize,
    parser: options?.parser,
    remarkPlugins: options?.remarkPlugins ?? PluginRemarkPlugins ?? [],
    rules: mergedRules,
    splitLineBreaks: options?.splitLineBreaks
  };
};

// src/lib/deserializer/utils/parseMarkdownBlocks.ts
import { marked } from "marked";
var parseMarkdownBlocks = (content, { exclude = ["space"], trim = true } = {}) => {
  let tokens = [...marked.lexer(content)];
  if (exclude.length > 0) {
    tokens = tokens.filter((token) => !exclude.includes(token.type));
  }
  if (trim) {
    tokens = tokens.map((token) => ({
      ...token,
      raw: token.raw.trimEnd()
    }));
  }
  return tokens;
};

// src/lib/deserializer/convertNodesDeserialize.ts
var convertNodesDeserialize = (nodes, deco, options) => {
  return nodes.reduce((acc, node) => {
    if (shouldIncludeNode2(node, options)) {
      acc.push(...buildSlateNode(node, deco, options));
    }
    return acc;
  }, []);
};
var buildSlateNode = (mdastNode, deco, options) => {
  if (mdastNode.type === "mdxJsxTextElement" || mdastNode.type === "mdxJsxFlowElement") {
    const result = customMdxDeserialize(mdastNode, deco, options);
    return Array.isArray(result) ? result : [result];
  }
  const type = getPlateNodeType(mdastNode.type);
  const nodeParser = getDeserializerByKey(type, options);
  if (nodeParser) {
    const result = nodeParser(mdastNode, deco, options);
    return Array.isArray(result) ? result : [result];
  }
  return [];
};
var shouldIncludeNode2 = (node, options) => {
  const { allowedNodes, allowNode, disallowedNodes } = options;
  if (!node.type) return true;
  const type = getPlateNodeType(node.type);
  if (allowedNodes && disallowedNodes && allowedNodes.length > 0 && disallowedNodes.length > 0) {
    throw new Error("Cannot combine allowedNodes with disallowedNodes");
  }
  if (allowedNodes) {
    if (!allowedNodes.includes(type)) {
      return false;
    }
  } else if (disallowedNodes?.includes(type)) {
    return false;
  }
  if (allowNode?.deserialize) {
    return allowNode.deserialize({
      ...node,
      type
    });
  }
  return true;
};

// src/lib/deserializer/convertChildrenDeserialize.ts
var convertChildrenDeserialize = (children, deco, options) => {
  if (children.length === 0) {
    return [{ text: "" }];
  }
  return convertNodesDeserialize(children, deco, options);
};

// src/lib/deserializer/convertTextsDeserialize.ts
var convertTextsDeserialize = (mdastNode, deco, options) => {
  return mdastNode.children.reduce((acc, n) => {
    acc.push(
      ...buildSlateNode(
        n,
        { ...deco, [getPlateNodeType(mdastNode.type)]: true },
        options
      )
    );
    return acc;
  }, []);
};

// src/lib/deserializer/deserializeMd.ts
import remarkParse from "remark-parse";
import { unified as unified3 } from "unified";

// src/lib/deserializer/mdastToSlate.ts
var mdastToSlate = (node, options) => {
  return buildSlateRoot(node, options);
};
var buildSlateRoot = (root, options) => {
  if (!options.splitLineBreaks) {
    return convertNodesDeserialize(root.children, {}, options);
  }
  const results = [];
  let startLine = root.position?.start.line ?? 1;
  const addEmptyParagraphs = (count) => {
    if (count > 0) {
      results.push(
        ...Array.from({ length: count }).map(() => ({
          children: [{ text: "" }],
          type: "p"
        }))
      );
    }
  };
  root.children?.forEach((child, index) => {
    const isFirstChild = index === 0;
    const isLastChild = index === root.children.length - 1;
    if (child.position) {
      const emptyLinesBefore = child.position.start.line - (isFirstChild ? startLine : startLine + 1);
      addEmptyParagraphs(emptyLinesBefore);
      const transformValue = convertNodesDeserialize([child], {}, options);
      results.push(...transformValue);
      if (isLastChild) {
        const emptyLinesAfter = root.position.end.line - child.position.end.line - 1;
        addEmptyParagraphs(emptyLinesAfter);
      }
      startLine = child.position.end.line;
    } else {
      const transformValue = convertNodesDeserialize([child], {}, options);
      results.push(...transformValue);
    }
  });
  return results;
};

// src/lib/deserializer/deserializeMd.ts
var deserializeMd = (editor, data, options) => {
  data = data.replaceAll("<br>", "<br />");
  const mergedOptions = getMergedOptionsDeserialize(editor, options);
  const toSlateProcessor = unified3().use(remarkParse).use(mergedOptions.remarkPlugins ?? []).use(remarkToSlate, mergedOptions);
  if (options?.memoize) {
    return parseMarkdownBlocks(data, options.parser).flatMap((token) => {
      if (token.type === "space") {
        return {
          ...editor.api.create.block(),
          _memo: token.raw
        };
      }
      return toSlateProcessor.processSync(token.raw).result.map((result) => {
        return {
          _memo: token.raw,
          ...result
        };
      });
    });
  }
  return toSlateProcessor.processSync(data).result;
};
var remarkToSlate = (
  // TODO: options
  function(options = {}) {
    this.compiler = function(node) {
      return mdastToSlate(node, options);
    };
  }
);

// src/lib/MarkdownPlugin.ts
var MarkdownPlugin = createTSlatePlugin({
  key: "markdown",
  options: {
    allowedNodes: null,
    disallowedNodes: null,
    remarkPlugins: [],
    rules: null
  }
}).extendApi(({ editor }) => ({
  deserialize: bindFirst(deserializeMd, editor),
  serialize: bindFirst(serializeMd, editor)
})).extend(({ api }) => ({
  parser: {
    format: "text/plain",
    deserialize: ({ data }) => api.markdown.deserialize(data),
    query: ({ data, dataTransfer }) => {
      const htmlData = dataTransfer.getData("text/html");
      if (htmlData) return false;
      const { files } = dataTransfer;
      if (!files?.length && // if content is simply a URL pass through to not break LinkPlugin
      isUrl(data)) {
        return false;
      }
      return true;
    }
  }
}));
export {
  MarkdownPlugin,
  basicMarkdownMarks,
  buildMdastNode,
  buildSlateNode,
  convertChildrenDeserialize,
  convertNodesDeserialize,
  convertNodesSerialize,
  convertTextsDeserialize,
  convertTextsSerialize,
  customMdxDeserialize,
  defaultRules,
  deserializeInlineMd,
  deserializeMd,
  getCustomMark,
  getDeserializerByKey,
  getMdAstNodeType,
  getMergedOptionsDeserialize,
  getMergedOptionsSerialize,
  getPlateNodeType,
  getSerializerByKey,
  indentListToMdastTree,
  mdastToSlate,
  parseMarkdownBlocks,
  serializeInlineMd,
  serializeMd,
  stripMarkdown,
  stripMarkdownBlocks,
  stripMarkdownInline,
  unreachable
};
//# sourceMappingURL=index.mjs.map